import { useState } from 'react';
import { useFitnessStore } from '../../../store/fitnessStore';
import { useCanAccess, useAILimit } from '../../../hooks/useAccess';
import { generateSportPlan } from '../../../services/anthropic';
import { getPexelsImage } from '../../../services/pexels';
import { useExercisesByBodyPart } from '../../../hooks/useExercises';
import { SPORTS, muscleToBodyPart } from '../../../utils/sportsData';
import MuscleMap from '../shared/MuscleMap';
import ExerciseCard from '../shared/ExerciseCard';
import PaywallBanner from '../shared/PaywallBanner';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';

export default function SportScreen() {
  const profile = useFitnessStore((s) => s.profile);
  const openModal = useFitnessStore((s) => s.openModal);
  const incrementAIUsage = useFitnessStore((s) => s.incrementAIUsage);
  const setProfile = useFitnessStore((s) => s.setProfile);
  const [selectedSport, setSelectedSport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const canSportPlans = useCanAccess('sport_plans');
  const { canUse: canUsePlan } = useAILimit('plans');

  const { data: heroImage } = useQuery({
    queryKey: ['pexels', selectedSport?.pexelsQuery],
    queryFn: () => getPexelsImage(selectedSport.pexelsQuery),
    enabled: !!selectedSport?.pexelsQuery,
    staleTime: 1000 * 60 * 60,
  });

  const bodyPart = selectedSport ? muscleToBodyPart(selectedSport.primaryMuscles?.[0]) : null;
  const { data: exercises = [] } = useExercisesByBodyPart(bodyPart, 8);

  async function handleGenerateSportPlan() {
    if (!canSportPlans || !canUsePlan) { openModal('upgrade'); return; }
    setGenerating(true);
    try {
      const plan = await generateSportPlan({ sport: selectedSport, profile });
      openModal('plan', plan);
      incrementAIUsage('plans');
    } catch {
      alert('Could not generate plan. Check your API key.');
    } finally {
      setGenerating(false);
    }
  }

  function handleSelectSport(sport) {
    setSelectedSport(sport);
    setProfile({ sport: sport.id });
  }

  if (selectedSport) {
    return (
      <div style={{ padding: '0 0 0' }}>
        <div style={{ position: 'relative', height: 200, overflow: 'hidden' }}>
          {heroImage?.url ? (
            <img src={heroImage.url} alt={selectedSport.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: '#1C1C1F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 60 }}>{selectedSport.emoji}</div>
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(10,10,11,0.3), rgba(10,10,11,0.95))' }} />
          <button type="button" onClick={() => setSelectedSport(null)} style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#fff', backdropFilter: 'blur(4px)' }}>
            <ArrowLeft size={18} />
          </button>
          {heroImage && (
            <div style={{ position: 'absolute', bottom: 6, right: 10, fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>
              Photo by {heroImage.photographer} on Pexels
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16 }}>
            <div style={{ fontFamily: 'Bebas Neue', fontSize: 32, lineHeight: 1 }}>{selectedSport.name.toUpperCase()}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 20, background: 'rgba(232,255,71,0.15)', color: '#E8FF47', fontWeight: 600 }}>{selectedSport.category}</span>
              <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 20, background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>{selectedSport.energySystem}</span>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 16px' }}>
          <div style={{ marginBottom: 16 }}>
            <div className="fit-section-label">MUSCLES TARGETED</div>
            <div className="fit-card">
              <MuscleMap
                primaryMuscles={selectedSport.primaryMuscles}
                secondaryMuscles={selectedSport.secondaryMuscles}
                onMuscleClick={() => {}}
              />
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedSport.primaryMuscles.map((m) => (
                  <span key={m} className="fit-tag fit-tag-lime">{m}</span>
                ))}
                {selectedSport.secondaryMuscles.map((m) => (
                  <span key={m} className="fit-tag fit-tag-orange">{m}</span>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div className="fit-section-label">TRACK THESE METRICS</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {selectedSport.metrics?.map((m) => (
                <div key={m} className="fit-card-sm" style={{ fontSize: 12 }}>{m}</div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            {canSportPlans ? (
              <div style={{ background: 'linear-gradient(135deg,rgba(232,255,71,0.08),rgba(10,10,11,0))', border: '1px solid rgba(232,255,71,0.2)', borderRadius: 16, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>✨ AI 4-Week Training Block</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>
                  Personalised to your weight, level & schedule. Periodised: accumulation → intensification → realisation → deload.
                </div>
                <button
                  type="button"
                  onClick={handleGenerateSportPlan}
                  disabled={generating}
                  className="fit-btn-primary"
                  style={{ opacity: generating ? 0.6 : 1 }}
                >
                  {generating ? '⏳ Generating Your Plan...' : `Generate ${selectedSport.name} Plan`}
                </button>
              </div>
            ) : (
              <PaywallBanner feature={`${selectedSport.name} AI training plans`} requiredTier="pro">
                <div className="fit-card">
                  <div style={{ fontSize: 13, fontWeight: 600 }}>AI Sport Training Plans</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>4-week periodised plan</div>
                </div>
              </PaywallBanner>
            )}
          </div>

          <div>
            <div className="fit-section-label">KEY EXERCISES</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {exercises.slice(0, 5).map((ex) => <ExerciseCard key={ex.id} exercise={ex} compact />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 16px 0' }}>
      <div style={{ fontFamily: 'Bebas Neue', fontSize: 30, marginBottom: 4 }}>SPORT MODE</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>Select your sport — get a complete training system</div>
      <div className="fit-grid-2">
        {SPORTS.map((sport) => (
          <motion.div
            key={sport.id}
            whileTap={{ scale: 0.97 }}
            onClick={() => handleSelectSport(sport)}
            style={{
              background: '#141416',
              border: `1px solid ${profile.sport === sport.id ? 'rgba(232,255,71,0.25)' : 'rgba(255,255,255,0.06)'}`,
              borderRadius: 14, padding: 14, cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 8 }}>{sport.emoji}</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{sport.name}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>{sport.energySystem} · {sport.category}</div>
            <span className={`fit-tag ${profile.sport === sport.id ? 'fit-tag-lime' : 'fit-tag-gray'}`}>
              {profile.sport === sport.id ? 'Active Sport' : 'Select →'}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
